import { useRef } from "react";
import type { N64Controls } from "../movement";
import MoveControl, { directions } from "./MoveControl";

// EmulatorJS's N64 inputs (see N64_CONTROL in ejs.ts): the analog stick is
// 16-19 (right, left, down, up), each 0 to full deflection; the D-pad 4-7
// (up, down, left, right), each on or off.
const STICK = { right: 16, left: 17, down: 18, up: 19 };
const DPAD = { up: 4, down: 5, left: 6, right: 7 };
const FULL = 0x7fff;

/**
 * Our movement controls standing in for an N64 game's analog stick and D-pad
 * when Settings picks one of ours for them; the N64 pad's own are hidden
 * then (CSS). The D-pad sits above the stick, as EmulatorJS places its own.
 */
export default function N64Move({
  controls,
  input,
  active,
  turboLocked,
  onTurbo,
  onTurboLock,
}: {
  controls: N64Controls;
  input: (index: number, value: number) => void;
  active: boolean;
  turboLocked: boolean;
  onTurbo: (down: boolean) => void;
  onTurboLock: () => void;
}) {
  const pressed = useRef(new Set<number>());
  const ours = (mode: N64Controls["stick"]) => mode !== "native" && mode !== "hidden";

  const stick = (x: number, y: number) => {
    input(STICK.right, x > 0 ? Math.round(FULL * x) : 0);
    input(STICK.left, x < 0 ? Math.round(FULL * -x) : 0);
    input(STICK.down, y > 0 ? Math.round(FULL * y) : 0);
    input(STICK.up, y < 0 ? Math.round(FULL * -y) : 0);
  };
  const dpad = (x: number, y: number) => {
    const d = directions(x, y);
    for (const [dir, index] of Object.entries(DPAD) as [keyof typeof DPAD, number][]) {
      const on = d[dir];
      if (on === pressed.current.has(index)) continue;
      if (on) pressed.current.add(index);
      else pressed.current.delete(index);
      input(index, on ? 1 : 0);
    }
  };

  if (!ours(controls.stick) && !ours(controls.dpad)) return null;
  const shared = { active, turboLocked, onTurbo, onTurboLock };
  return (
    <div className="n64-move">
      {ours(controls.dpad) && (
        <MoveControl {...shared} mode={controls.dpad as Exclude<typeof controls.dpad, "native" | "hidden">} onMove={dpad} label="N64 D-pad" />
      )}
      {ours(controls.stick) && (
        <MoveControl {...shared} mode={controls.stick as Exclude<typeof controls.stick, "native" | "hidden">} onMove={stick} label="N64 analog stick" />
      )}
    </div>
  );
}
